import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, InputNumber, message, Row, Select, Space,
  Statistic, Table, Tag, Typography
} from 'antd';
import { CheckCircleTwoTone, SaveOutlined, SyncOutlined } from '@ant-design/icons';
import { ErpMasterShell } from '../../../components/common/erp-grid';
import { accountsApi } from './accountsApi';
import './accounts.css';

const { Title, Text, Paragraph } = Typography;

// Stage 6B.2 — Opening Balances page.
// Reads from ledger_accounts.opening_balance + opening_balance_type.
// Operator can edit per row (inline), see total debit / total credit /
// difference, and optionally CONFIRM the trial balance (which flips
// accounting_settings.opening_balances_confirmed → true).
//
// NOT a posting engine. The opening voucher posting flow ships in a
// later stage; for now the values simply persist on the ledger row.

const BALANCE_TYPES = [
  { value: '', label: '—' },
  { value: 'debit',  label: 'Debit' },
  { value: 'credit', label: 'Credit' }
];

function fmtRupee(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function OpeningBalancesPage() {
  const [rows, setRows]       = useState([]);
  const [totals, setTotals]   = useState({ totalDebit: 0, totalCredit: 0, difference: 0, balanced: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await accountsApi.listOpeningBalances();
      setRows(data.rows || []);
      setTotals(data.totals || { totalDebit: 0, totalCredit: 0, difference: 0, balanced: true });
      setDirtyIds(new Set());
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load opening balances.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Recompute trial-balance totals locally so the operator sees the
  // result of unsaved edits without a round-trip.
  const liveTotals = useMemo(() => {
    let d = 0, c = 0;
    for (const r of rows) {
      const amt = Number(r.openingBalance) || 0;
      if (amt <= 0) continue;
      if (r.openingBalanceType === 'debit')  d += amt;
      if (r.openingBalanceType === 'credit') c += amt;
    }
    d = Math.round(d * 100) / 100;
    c = Math.round(c * 100) / 100;
    return { totalDebit: d, totalCredit: c, difference: Math.round((d - c) * 100) / 100, balanced: d === c };
  }, [rows]);

  const patchRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
    setDirtyIds((prev) => { const n = new Set(prev); n.add(id); return n; });
  }, []);

  const save = useCallback(async (confirm) => {
    setSaving(true);
    try {
      const payload = {
        confirm: confirm === true,
        rows: rows
          .filter((r) => dirtyIds.has(r.id))
          .map((r) => ({
            ledgerAccountId:    r.id,
            openingBalance:     Number(r.openingBalance) || 0,
            openingBalanceType: r.openingBalanceType || null
          }))
      };
      if (payload.rows.length === 0 && !confirm) {
        message.info('No changes to save.');
        setSaving(false);
        return;
      }
      // If we're only confirming with no edits, the backend tolerates an
      // empty rows[] iff confirm=true — but our validator requires at
      // least one row, so we always send all dirty rows OR one no-op row
      // to keep schema happy.
      if (payload.rows.length === 0 && confirm) {
        payload.rows.push({
          ledgerAccountId: rows[0].id,
          openingBalance:  Number(rows[0].openingBalance) || 0,
          openingBalanceType: rows[0].openingBalanceType || null
        });
      }
      const data = await accountsApi.updateOpeningBalances(payload);
      setRows(data.rows || []);
      setTotals(data.totals || liveTotals);
      setDirtyIds(new Set());
      message.success(confirm ? 'Opening balances saved & confirmed.' : 'Opening balances saved.');
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save opening balances.');
    } finally { setSaving(false); }
  }, [rows, dirtyIds, liveTotals]);

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'accountCode', key: 'accountCode',
      width: 140, fixed: 'left',
      render: (v) => <code className="erp-grid__code">{v}</code> },
    { title: 'Name', dataIndex: 'accountName', key: 'accountName',
      width: 240 },
    { title: 'Group', dataIndex: 'accountGroup', key: 'accountGroup',
      width: 160 },
    { title: 'Type', dataIndex: 'accountType', key: 'accountType',
      width: 100,
      render: (v) => <Tag color="default" style={{ marginInlineEnd: 0 }}>{v}</Tag> },
    { title: 'Normal', dataIndex: 'normalBalance', key: 'normalBalance',
      width: 80, align: 'center' },
    { title: 'Opening Balance', dataIndex: 'openingBalance', key: 'openingBalance',
      width: 170, align: 'right',
      render: (v, row) => (
        <InputNumber
          min={0}
          step={1}
          style={{ width: '100%' }}
          value={v}
          formatter={(n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0}
          onChange={(n) => patchRow(row.id, { openingBalance: Number(n) || 0 })}
        />
      ) },
    { title: 'Dr / Cr', dataIndex: 'openingBalanceType', key: 'openingBalanceType',
      width: 120,
      render: (v, row) => (
        <Select
          style={{ width: '100%' }}
          value={v || ''}
          options={BALANCE_TYPES}
          onChange={(val) => patchRow(row.id, { openingBalanceType: val || null })}
        />
      ) },
    { title: 'Sys', dataIndex: 'isSystem', key: 'isSystem',
      width: 50, align: 'center',
      render: (v) => v ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : null }
  ]), [patchRow]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Opening Balances</Title>
      <Alert type="info" banner showIcon style={{ marginBottom: 10 }}
        message="Trial balance — enter starting balances per ledger. Save when ready, then Confirm to lock in. The opening voucher posting flow ships in a later stage." />
      <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="Total Debit (₹)"  value={liveTotals.totalDebit}
              precision={2} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="Total Credit (₹)" value={liveTotals.totalCredit}
              precision={2} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="Difference (₹)"   value={Math.abs(liveTotals.difference)}
              precision={2}
              valueStyle={{ color: liveTotals.balanced ? '#3f8600' : '#cf1322' }}
              suffix={liveTotals.balanced ? '✓ Balanced' : '✗ Imbalanced'} />
          </Card>
        </Col>
      </Row>
      {!liveTotals.balanced ? (
        <Alert type="warning" showIcon style={{ marginBottom: 10 }}
          message={`Trial balance is out by ₹ ${fmtRupee(Math.abs(liveTotals.difference))}.`}
          description={'Total debits should equal total credits. Review the rows below — the imbalance must be cleared before the posting engine accepts business-day transactions in a later stage.'} />
      ) : null}
      <Space style={{ marginBottom: 10 }} wrap>
        <Button size="small" icon={<SyncOutlined />} onClick={refresh}>Reload</Button>
        <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving}
                onClick={() => save(false)} disabled={dirtyIds.size === 0}>
          Save {dirtyIds.size > 0 ? `(${dirtyIds.size} changed)` : ''}
        </Button>
        <Button size="small" onClick={() => save(true)} loading={saving} disabled={!liveTotals.balanced}>
          Save & Confirm
        </Button>
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          Confirm flips <code>accounting_settings.opening_balances_confirmed</code>.
          Only available when the trial balance is in agreement.
        </Text>
      </Space>
      <Table
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 25, size: 'small', showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
      />
      <Paragraph type="secondary" style={{ marginTop: 10, fontSize: 11.5 }}>
        Tip: system ledgers can carry opening balances when relevant
        (e.g. <Text code>CASH</Text>, <Text code>BANK</Text>, <Text code>SUNDRY-DEBTORS</Text>).
        Customer / supplier sub-ledger balances will be added when the
        receivables / payables module ships.
      </Paragraph>
      </div>
    </ErpMasterShell>
  );
}
